import { ResizeManager } from '@theme/event-manager';
import Lenis from '@lib/lenis';

/*
class LenisSmoothScroll {
  constructor() {
    this.enabled = document.body.dataset.smoothScroll === 'true';

    // Disable Lenis on touch devices immediately
    if (this.isTouchDevice()) {
      this.enabled = false;
    }

    window.lenis = null;
    this.lenis = null;
    this.rafHandle = null;

    if (this.enabled && typeof Lenis !== 'undefined') {
      this.init();
    }
  }

  isTouchDevice() {
    return window.matchMedia('(pointer: coarse)').matches;
  }

  init() {
    this.lenis = new Lenis({
      lerp: 0.1,
      smoothWheel: true,
      smoothTouch: false, // just in case
      infinite: false,
      gestureDirection: 'vertical',
      wheelMultiplier: 1.1,
    });

    window.lenis = this.lenis;
    this.startRAF();
    this.addEventListeners();
  }

  startRAF() {
    if (this.rafHandle) return;

    const raf = (time) => {
      if (!this.lenis) return;
      this.lenis.raf(time);
      this.rafHandle = requestAnimationFrame(raf);
    };

    this.rafHandle = requestAnimationFrame(raf);
  }

  addEventListeners() {
    this.lenis.on('scroll', this.onScroll);
    ResizeManager.subscribe(this.onResize);
  }

  onScroll = (e) => {};

  onResize = () => {
    this.lenis?.resize();
  };

  destroy() {
    if (this.rafHandle) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }

    if (this.lenis) {
      this.lenis.off('scroll', this.onScroll);
      this.lenis.destroy();
    }

    ResizeManager.unsubscribe(this.onResize);

    window.lenis = null;
    this.lenis = null;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.lenisManager = new LenisSmoothScroll();
});

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', () => {
    window.lenisManager?.destroy();
    window.lenisManager = new LenisSmoothScroll();
  });
}
*/

class LenisSmoothScroll {
  constructor() {
    // Global flags
    this.lenis = null;
    this.rafHandle = null;

    // Bind methods once
    this.onScroll = this.onScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.isTouchDeviceManage = this.isTouchDevice();

    // Initial setup based on current state
    if (this.shouldEnable()) {
      this.init();
    }

    // Always listen to resize so we can toggle on/off when breakpoint changes
    ResizeManager.subscribe(this.handleResize);
  }

  // Touch detection
  isTouchDevice() {
    return window.matchMedia('(pointer: coarse)').matches;
  }

  // Viewport / device–based condition
  shouldEnable() {
    // Respect theme setting
    const smoothFlag = document.body.dataset.smoothScroll === 'true';
    if (!smoothFlag) return false;

    // Disable completely on touch devices
    if (this.isTouchDevice()) return false;

    // Optional: only enable on "desktop" widths
    // Adjust breakpoint if you want
    const DESKTOP_MIN_WIDTH = 768;
    if (window.innerWidth < DESKTOP_MIN_WIDTH) return false;

    return true;
  }

  init() {
    if (this.lenis) return; // already active, safety guard

    // this.lenis = new Lenis({
    //   lerp: 0.1,
    //   smoothWheel: true,
    //   smoothTouch: false,
    //   infinite: false,
    //   gestureDirection: 'vertical',
    //   wheelMultiplier: 1.1,
    // });

    this.lenis = new Lenis({
      duration: 2.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: !this.isTouchDeviceManage, // Disable smooth scrolling on mobile and tablet
      mouseMultiplier: 1,
      smoothTouch: false, // Optional: Disable touch smoothing on mobile
      touchMultiplier: 2,
      infinite: false,
    });

    // Expose globally if needed
    window.lenis = this.lenis;

    this.startRAF();
    this.addEventListeners();
  }

  startRAF() {
    if (this.rafHandle) return;

    const raf = (time) => {
      if (!this.lenis) return;
      this.lenis.raf(time);
      this.rafHandle = requestAnimationFrame(raf);
    };

    this.rafHandle = requestAnimationFrame(raf);
  }

  addEventListeners() {
    if (!this.lenis) return;
    this.lenis.on('scroll', this.onScroll);
  }

  onScroll(e) {
    // Custom scroll effects (if needed)
  }

  // Called on any resize via ResizeManager
  handleResize() {
    const shouldBeEnabled = this.shouldEnable();

    // Case 1: should be enabled but currently off → init
    if (shouldBeEnabled && !this.lenis) {
      this.init();
      return;
    }

    // Case 2: should be disabled but currently on → disable
    if (!shouldBeEnabled && this.lenis) {
      this.disableLenisInstance();
      return;
    }

    // Case 3: still enabled → just resize the Lenis instance
    if (shouldBeEnabled && this.lenis) {
      this.lenis.resize();
    }
  }

  // Internal cleanup (but keep resize observer alive)
  disableLenisInstance() {
    if (this.rafHandle) {
      cancelAnimationFrame(this.rafHandle);
      this.rafHandle = null;
    }

    if (this.lenis) {
      this.lenis.off('scroll', this.onScroll);
      this.lenis.destroy();
      this.lenis = null;
    }

    window.lenis = null;
  }

  // Full destroy (for Shopify designMode / section:load reset)
  destroy() {
    this.disableLenisInstance();

    // Stop listening to ResizeManager completely
    ResizeManager.unsubscribe(this.handleResize);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.lenisManager = new LenisSmoothScroll();
});

// Handle theme editor changes
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', () => {
    window.lenisManager?.destroy();
    window.lenisManager = new LenisSmoothScroll();
  });
}
